// src/Pages/Admin/Sesion/AdminSesiones.js
// Panel admin de sesiones.
//
// `sesiones.usuario_id` no tiene FK declarada en Postgres, así que el embed
// `usuario_asignado:usuario_id (...)` falla con "Could not find a relationship".
// Por eso cargamos las sesiones planas y resolvemos el usuario en el cliente.
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, LogOut } from "lucide-react";
import { supabase } from "../../../Supabase/SupabaseClient";
import { listarFotos, borrarFoto } from "../../../utils/fotosService";
import NuevaSesionForm from "./NuevaSesionForm";
import SesionSelector from "./SesionSelector";
import SesionDetails from "./SesionDetails";
import FotoUploader from "./FotoUploader";
import FotosGrid from "./FotosGrid";
import AlertMessage from "./AlertMessage";
import LoadingSpinner from "./LoadingSpinner";
import "./AdminSesiones.css";

const SELECT_SESION = "*";

const SESION_VACIA = {
  nombre: "",
  fecha: "",
  usuario_id: "",
  estado: "pendiente",
  descripcion: "",
};

const AdminSesiones = () => {
  const navigate = useNavigate();

  const [sesiones, setSesiones] = useState([]);
  const [sesionSeleccionada, setSesionSeleccionada] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingFotos, setLoadingFotos] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [detallesError, setDetallesError] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [nuevaSesion, setNuevaSesion] = useState(SESION_VACIA);

  // Espejo de `usuarios` para resolver el asignado dentro de los loaders, que se
  // ejecutan en el mismo ciclo en que se acaba de setear el estado.
  const usuariosRef = useRef([]);

  const descripcionError = (err) => {
    if (!err) return null;
    if (typeof err === "string") return err;
    if (err.code === "PGRST116") {
      return (
        "La sesión no se actualizó porque las políticas RLS ocultaron la fila " +
        "(0 filas afectadas). Suele ser una política UPDATE sin USING."
      );
    }
    return err.message || err.details || JSON.stringify(err);
  };

  // Adjunta `usuario_asignado` a una sesión usando el listado ya cargado
  const conUsuarioAsignado = useCallback((sesion) => {
    if (!sesion) return sesion;
    if (!sesion.usuario_id) return { ...sesion, usuario_asignado: null };
    const encontrado = usuariosRef.current.find(
      (u) => String(u.id) === String(sesion.usuario_id)
    );
    return { ...sesion, usuario_asignado: encontrado || null };
  }, []);

  const cargarUsuarios = useCallback(async () => {
    const { data, error: err } = await supabase
      .from("usuarios")
      .select("id, email, nombre, rol")
      .order("nombre", { ascending: true });

    if (err) throw err;

    usuariosRef.current = data || [];
    setUsuarios(data || []);
  }, []);

  // Acepta un id o el objeto sesión completo: el selector hace click sobre la
  // fila y pasa el objeto, mientras que los demás llamadores pasan `.id`.
  const cargarFotosSesion = useCallback(
    async (sesionOId) => {
      const sesionId = sesionOId?.id ?? sesionOId;

      if (!sesionId || typeof sesionId !== "string") {
        console.error("cargarFotosSesion recibió un id inválido:", sesionOId);
        setSesionSeleccionada(null);
        return;
      }

      setLoadingFotos(true);
      setDetallesError(null);
      try {
        const { data: sesion, error: errSesion } = await supabase
          .from("sesiones")
          .select(SELECT_SESION)
          .eq("id", sesionId)
          .single();

        if (errSesion) throw errSesion;

        const fotos = await listarFotos(sesionId);
        setSesionSeleccionada({ ...conUsuarioAsignado(sesion), fotos });
      } catch (err) {
        console.error("Error al cargar fotos de la sesión:", err);
        setDetallesError(descripcionError(err));
        setError("No se pudieron cargar las fotos de esta sesión");
        setSesionSeleccionada((prev) => (prev ? { ...prev, fotos: [] } : null));
      } finally {
        setLoadingFotos(false);
      }
    },
    [conUsuarioAsignado]
  );

  const cargarSesiones = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDetallesError(null);
    try {
      const { data, error: err } = await supabase
        .from("sesiones")
        .select(SELECT_SESION)
        .order("fecha", { ascending: false });

      if (err) throw err;

      const list = (data || []).map(conUsuarioAsignado);
      setSesiones(list);

      const actual = list.find((s) => s.id === sesionSeleccionada?.id) || list[0];
      setSesionSeleccionada(null);
      if (actual) await cargarFotosSesion(actual.id);
    } catch (err) {
      console.error("Error al cargar sesiones:", err);
      setError("No se pudieron cargar las sesiones");
      setDetallesError(descripcionError(err));
      setSesiones([]);
      setSesionSeleccionada(null);
    } finally {
      setLoading(false);
    }
  }, [conUsuarioAsignado, cargarFotosSesion, sesionSeleccionada?.id]);

  const verificarAdmin = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDetallesError(null);
    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError) throw authError;
      if (!user) {
        setError("Debes iniciar sesión como administrador para acceder a esta página");
        return;
      }

      const { data: adminData, error: adminError } = await supabase
        .from("usuarios")
        .select("rol")
        .eq("id", user.id)
        .single();

      if (adminError) throw adminError;

      if (!adminData || String(adminData.rol).toLowerCase() !== "admin") {
        setError("No tienes permisos de administrador para acceder a esta página");
        return;
      }

      await cargarUsuarios();
      await cargarSesiones();
    } catch (err) {
      console.error("Error al verificar permisos:", err);
      setError("No se pudo verificar tus permisos de administrador");
      setDetallesError(descripcionError(err));
      setSesiones([]);
      setUsuarios([]);
      setSesionSeleccionada(null);
    } finally {
      setLoading(false);
    }
  }, [cargarUsuarios, cargarSesiones]);

  useEffect(() => {
    verificarAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const crearSesion = async () => {
    setGuardando(true);
    setError(null);
    setDetallesError(null);
    try {
      const payload = {
        nombre: nuevaSesion.nombre.trim(),
        fecha: nuevaSesion.fecha,
        estado: nuevaSesion.estado || "pendiente",
        descripcion: nuevaSesion.descripcion?.trim() || null,
        usuario_id: nuevaSesion.usuario_id || null,
      };

      const { data, error: err } = await supabase
        .from("sesiones")
        .insert([payload])
        .select(SELECT_SESION)
        .single();

      if (err) throw err;

      setNuevaSesion(SESION_VACIA);
      setMostrarForm(false);
      setMensaje(`Sesión "${payload.nombre}" creada correctamente.`);

      await cargarSesiones();
      await cargarFotosSesion(data.id);
    } catch (err) {
      console.error("Error al crear sesión:", err);
      setError("No se pudo crear la sesión");
      setDetallesError(descripcionError(err));
    } finally {
      setGuardando(false);
    }
  };

  const eliminarSesion = async (sesion) => {
    if (
      !window.confirm(
        `¿Eliminar "${sesion.nombre || "esta sesión"}" y todas sus fotos? Esta acción no se puede deshacer.`
      )
    ) {
      return;
    }

    setError(null);
    setDetallesError(null);
    try {
      // Las fotos se borran una por una para que también salga del storage.
      const fotos = await listarFotos(sesion.id);

      const fallidas = [];
      for (const foto of fotos) {
        const res = await borrarFoto(foto);
        if (!res.ok) fallidas.push(`${foto.id}: ${res.error}`);
      }

      const { error: err } = await supabase
        .from("sesiones")
        .delete()
        .eq("id", sesion.id);

      if (err) throw err;

      const restantes = sesiones.filter((s) => s.id !== sesion.id);
      setSesiones(restantes);

      if (sesionSeleccionada?.id === sesion.id) {
        const siguiente = restantes[0];
        setSesionSeleccionada(null);
        if (siguiente) await cargarFotosSesion(siguiente.id);
      } else {
        await cargarSesiones();
      }

      setMensaje("Sesión eliminada correctamente.");
      if (fallidas.length > 0) {
        setDetallesError(
          `Estas fotos quedaron en la base y no se pudieron borrar: ${fallidas.join(" · ")}`
        );
      }
    } catch (err) {
      console.error("Error al eliminar sesión:", err);
      setError("No se pudo eliminar la sesión");
      setDetallesError(descripcionError(err));
    }
  };

  const actualizarUsuarioAsignado = async (sesionId, nuevoUsuarioId) => {
    setError(null);
    setDetallesError(null);
    try {
      const { data, error: err } = await supabase
        .from("sesiones")
        .update({ usuario_id: nuevoUsuarioId || null })
        .eq("id", sesionId)
        .select(SELECT_SESION)
        .single();

      if (err) throw err;

      const finalRow = conUsuarioAsignado(data);
      setSesiones((prev) => prev.map((s) => (s.id === sesionId ? finalRow : s)));

      if (sesionSeleccionada?.id === sesionId) {
        setSesionSeleccionada((prev) => ({ ...prev, ...finalRow }));
      }

      setMensaje("Cliente asignado actualizado.");
    } catch (err) {
      console.error("Error al actualizar usuario asignado:", err);
      setError("No se pudo actualizar el cliente asignado");
      setDetallesError(descripcionError(err));
      await cargarSesiones();
    }
  };

  const cambiarEstado = async (sesionId, nuevoEstado) => {
    if (!sesionId || !nuevoEstado) return;

    setGuardando(true);
    setError(null);
    setDetallesError(null);
    try {
      const { data, error: err } = await supabase
        .from("sesiones")
        .update({ estado: nuevoEstado })
        .eq("id", sesionId)
        .select(SELECT_SESION)
        .single();

      if (err) throw err;

      const finalRow = conUsuarioAsignado(data);
      setSesiones((prev) => prev.map((s) => (s.id === sesionId ? finalRow : s)));

      if (sesionSeleccionada?.id === sesionId) {
        setSesionSeleccionada((prev) => ({ ...prev, ...finalRow }));
      }

      setMensaje(
        `Estado actualizado a "${nuevoEstado.replace(/_/g, " ")}". Recordá marcar "completada" recién cuando las fotos estén subidas.`
      );
    } catch (err) {
      console.error("Error al cambiar el estado:", err);
      setError("No se pudo cambiar el estado de la sesión");
      setDetallesError(descripcionError(err));
      await cargarSesiones();
    } finally {
      setGuardando(false);
    }
  };

  const refrescarFotos = useCallback(() => {
    if (sesionSeleccionada?.id) cargarFotosSesion(sesionSeleccionada.id);
  }, [sesionSeleccionada?.id, cargarFotosSesion]);

  // Solo los clientes (no admins) pueden ser destinatarios de una sesión
  const usuariosCliente = useMemo(
    () => usuarios.filter((u) => String(u.rol ?? "").toLowerCase() !== "admin"),
    [usuarios]
  );

  if (loading) return <LoadingSpinner mensaje="Cargando panel de administración..." />;

  if (error && !sesiones.length) {
    return (
      <div className="sg">
        <div className="sg-error-state">
          <h3>{error}</h3>
          {detallesError && (
            <details>
              <summary>Ver detalles técnicos</summary>
              <pre>{detallesError}</pre>
            </details>
          )}
          <div className="sg-confirm-actions sg-confirm-actions--center">
            <button type="button" className="sg-btn" onClick={verificarAdmin}>
              Reintentar
            </button>
            <button
              type="button"
              className="sg-btn sg-btn--ghost"
              onClick={() => navigate("/")}
            >
              Volver al inicio
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sg">
      <header className="sg-head">
        <div>
          <h1 className="sg-title">Sesiones</h1>
          <p className="sg-subtitle">
            Subí, ordená y destacá las fotos de cada sesión. El cliente las ve exactamente
            como las dejes acá.
          </p>
        </div>

        <div className="sg-head-actions">
          <button
            type="button"
            className="sg-btn"
            onClick={() => setMostrarForm((v) => !v)}
          >
            <Plus size={16} /> Nueva sesión
          </button>
          <button
            type="button"
            className="sg-btn sg-btn--ghost"
            onClick={() => navigate("/admin")}
          >
            <LogOut size={15} /> Volver
          </button>
        </div>
      </header>

      {mensaje && (
        <AlertMessage tipo="exito" mensaje={mensaje} onCerrar={() => setMensaje(null)} />
      )}
      {error && (
        <AlertMessage tipo="error" mensaje={error} onCerrar={() => setError(null)} />
      )}
      {detallesError && !error && (
        <AlertMessage
          tipo="advertencia"
          mensaje={detallesError}
          onCerrar={() => setDetallesError(null)}
        />
      )}

      {mostrarForm && (
        <NuevaSesionForm
          nuevaSesion={nuevaSesion}
          setNuevaSesion={setNuevaSesion}
          usuariosCliente={usuariosCliente}
          loading={guardando}
          onCrear={crearSesion}
          onCancelar={() => {
            setNuevaSesion(SESION_VACIA);
            setMostrarForm(false);
          }}
        />
      )}

      <SesionSelector
        sesiones={sesiones}
        sesionActual={sesionSeleccionada}
        onSeleccionar={cargarFotosSesion}
        onCambiarUsuario={actualizarUsuarioAsignado}
        onCambiarEstado={cambiarEstado}
        usuariosCliente={usuariosCliente}
        onEliminar={eliminarSesion}
        guardando={guardando}
      />

      {sesionSeleccionada ? (
        <>
          <SesionDetails sesion={sesionSeleccionada} />

          <FotoUploader
            sesionId={sesionSeleccionada.id}
            disabled={loadingFotos}
            onSubidas={refrescarFotos}
          />

          <div className="sg-panel">
            <h3 className="sg-detail-title">Fotos</h3>
            <FotosGrid
              fotos={sesionSeleccionada.fotos || []}
              loading={loadingFotos}
              onChange={refrescarFotos}
            />
          </div>
        </>
      ) : (
        <div className="sg-empty">
          <p>Seleccioná una sesión para ver y editar sus fotos.</p>
        </div>
      )}
    </div>
  );
};

export default AdminSesiones;